import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Download, RefreshCw, Search, Loader2, ShieldAlert, Inbox, LayoutGrid } from 'lucide-react';
import AdminCMS from './AdminCMS';
import { API, ADMIN_TOKEN } from './adminConfig';

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function toCSV(rows) {
  const headers = [
    'Tarih', 'Firma', 'Firma Türü', 'Şehir', 'İletişim Kişi',
    'Telefon', 'E-posta', 'İlgilenilen Ürünler', 'Aylık İhtiyaç', 'Mesaj',
  ];
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v).replace(/"/g, '""');
    return `"${s}"`;
  };
  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push([
      formatDate(r.created_at),
      r.company_name,
      r.company_type,
      r.city,
      r.contact_person,
      r.phone,
      r.email,
      (r.interested_products || []).join(' · '),
      r.monthly_need || '',
      r.message || '',
    ].map(escape).join(','));
  }
  return lines.join('\n');
}

export default function AdminLeads() {
  const [tab, setTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchLeads = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/horeca/leads`, {
        params: { token: ADMIN_TOKEN, limit: 1000 },
      });
      setLeads(res.data || []);
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? 'Yetkisiz erişim. Token doğrulaması başarısız.'
          : 'Talepler yüklenirken bir sorun oluştu.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Arama motorlarına kapalı
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive, nosnippet';
    document.head.appendChild(meta);
    document.title = 'Panel · Lutfiye Horeca';
    fetchLeads();
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return leads;
    const q = query.toLowerCase().trim();
    return leads.filter((l) => {
      const bag = [
        l.company_name, l.contact_person, l.phone, l.email,
        l.company_type, l.city, l.monthly_need, l.message,
        ...(l.interested_products || []),
      ].filter(Boolean).join(' ').toLowerCase();
      return bag.includes(q);
    });
  }, [leads, query]);

  const downloadCSV = () => {
    const csv = toCSV(filtered);
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lutfiye-horeca-talepler-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      data-testid="admin-leads-page"
      className="min-h-screen bg-[#FBF7EE] text-[#1F1F1F] font-body"
    >
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#1F1F1F] text-[#FBF7EE] border-b border-[#B08834]/25">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-4 h-4 text-[#B08834]" />
            <span className="eyebrow text-[#B08834]">Gizli Panel</span>
            <span className="text-sm font-body">Lutfiye Horeca — Talepler</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-1 bg-[#FBF7EE]/5 border border-[#FBF7EE]/15 p-0.5">
              <button
                onClick={() => setTab('leads')}
                data-testid="admin-tab-leads"
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] transition-colors ${
                  tab === 'leads' ? 'bg-[#B08834] text-[#1F1F1F]' : 'text-[#FBF7EE]/80 hover:text-[#B08834]'
                }`}
              >
                <Inbox className="w-3 h-3" />
                Başvurular
              </button>
              <button
                onClick={() => setTab('cms')}
                data-testid="admin-tab-cms"
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] transition-colors ${
                  tab === 'cms' ? 'bg-[#B08834] text-[#1F1F1F]' : 'text-[#FBF7EE]/80 hover:text-[#B08834]'
                }`}
              >
                <LayoutGrid className="w-3 h-3" />
                Kategoriler
              </button>
            </div>
            {tab === 'leads' && (
              <>
                <span className="text-xs text-[#FBF7EE]/70 font-body hidden md:inline">
                  {loading ? 'Yükleniyor…' : `${leads.length} kayıt`}
                </span>
                <button
                  onClick={fetchLeads}
                  className="text-xs uppercase tracking-[0.2em] text-[#FBF7EE]/80 hover:text-[#B08834] flex items-center gap-2"
                  data-testid="admin-refresh-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                  Yenile
                </button>
                <button
                  onClick={downloadCSV}
                  disabled={!filtered.length}
                  className="inline-flex items-center gap-2 bg-[#B08834] hover:bg-[#8f6d2a] disabled:opacity-40 disabled:cursor-not-allowed text-[#1F1F1F] px-4 py-2 text-xs tracking-[0.18em]"
                  data-testid="admin-export-btn"
                >
                  <Download className="w-3.5 h-3.5" />
                  CSV
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        {/* Mobile tabs */}
        <div className="md:hidden mb-6 flex bg-white border border-[#1F1F1F]/15">
          <button
            onClick={() => setTab('leads')}
            data-testid="admin-tab-leads-mobile"
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-[11px] uppercase tracking-[0.18em] ${
              tab === 'leads' ? 'bg-[#1F1F1F] text-[#FBF7EE]' : 'text-[#5A5145]'
            }`}
          >
            <Inbox className="w-3 h-3" />
            Başvurular
          </button>
          <button
            onClick={() => setTab('cms')}
            data-testid="admin-tab-cms-mobile"
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-[11px] uppercase tracking-[0.18em] ${
              tab === 'cms' ? 'bg-[#1F1F1F] text-[#FBF7EE]' : 'text-[#5A5145]'
            }`}
          >
            <LayoutGrid className="w-3 h-3" />
            Kategoriler
          </button>
        </div>

        {tab === 'cms' && <AdminCMS />}

        {tab === 'leads' && (
        <>
        {/* Search */}
        <div className="mb-6 flex items-center gap-3">
          <div className="flex-1 flex items-center gap-3 bg-white border border-[#1F1F1F]/15 px-4 py-3">
            <Search className="w-4 h-4 text-[#5A5145]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Firma, kişi, e-posta, şehir, ürün ile ara…"
              className="flex-1 bg-transparent outline-none text-sm font-body"
              data-testid="admin-search-input"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="text-xs uppercase tracking-[0.18em] text-[#B03A2E] hover:text-[#8A2E22]"
              >
                Temizle
              </button>
            )}
          </div>
          <div className="hidden md:block text-xs text-[#5A5145] font-body">
            {filtered.length} / {leads.length}
          </div>
        </div>

        {/* States */}
        {loading && (
          <div className="py-24 flex flex-col items-center text-[#5A5145]">
            <Loader2 className="w-6 h-6 animate-spin mb-3" />
            Yükleniyor…
          </div>
        )}

        {!loading && error && (
          <div
            data-testid="admin-error"
            className="py-24 flex flex-col items-center text-[#B03A2E]"
          >
            <ShieldAlert className="w-8 h-8 mb-3" />
            <div className="text-sm font-body">{error}</div>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="py-24 text-center text-[#5A5145] font-body">
            {leads.length === 0 ? 'Henüz talep yok.' : 'Aramaya uyan kayıt yok.'}
          </div>
        )}

        {/* Table */}
        {!loading && !error && filtered.length > 0 && (
          <div className="bg-white border border-[#1F1F1F]/12 overflow-x-auto">
            <table className="min-w-full text-sm font-body" data-testid="admin-leads-table">
              <thead className="bg-[#EDE5D2] text-[#1F1F1F]">
                <tr>
                  <th className="text-left px-4 py-3 eyebrow text-[#5A5145]">Tarih</th>
                  <th className="text-left px-4 py-3 eyebrow text-[#5A5145]">Firma</th>
                  <th className="text-left px-4 py-3 eyebrow text-[#5A5145]">Tür / Şehir</th>
                  <th className="text-left px-4 py-3 eyebrow text-[#5A5145]">İletişim</th>
                  <th className="text-left px-4 py-3 eyebrow text-[#5A5145]">Ürünler</th>
                  <th className="text-left px-4 py-3 eyebrow text-[#5A5145]">Hacim</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr
                    key={l.id}
                    onClick={() => setSelected(l)}
                    className="border-t border-[#1F1F1F]/8 hover:bg-[#FBF7EE] cursor-pointer"
                    data-testid={`admin-lead-row-${l.id}`}
                  >
                    <td className="px-4 py-3 text-xs text-[#5A5145] whitespace-nowrap">
                      {formatDate(l.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#1F1F1F]">{l.company_name}</div>
                      <div className="text-xs text-[#5A5145]">{l.contact_person}</div>
                    </td>
                    <td className="px-4 py-3 text-[#1F1F1F]">
                      <div>{l.company_type}</div>
                      <div className="text-xs text-[#5A5145]">{l.city}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[#1F1F1F]">{l.email}</div>
                      <div className="text-xs text-[#5A5145]">{l.phone}</div>
                    </td>
                    <td className="px-4 py-3 text-[#5A5145]">
                      {(l.interested_products || []).length > 0
                        ? (l.interested_products || []).join(' · ')
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-[#5A5145]">{l.monthly_need || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-[#1F1F1F]/60 flex items-end md:items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="admin-lead-modal"
        >
          <div
            className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 md:p-8 border-b border-[#1F1F1F]/12">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="eyebrow text-[#B03A2E] mb-1">Talep Detayı</div>
                  <div className="font-display text-2xl md:text-3xl">
                    {selected.company_name}
                  </div>
                  <div className="text-sm text-[#5A5145] mt-1">
                    {selected.company_type} · {selected.city}
                  </div>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  className="text-xs uppercase tracking-[0.2em] text-[#5A5145] hover:text-[#B03A2E]"
                >
                  Kapat ✕
                </button>
              </div>
            </div>

            <div className="p-6 md:p-8 grid gap-5 text-sm">
              <Row label="Tarih" value={formatDate(selected.created_at)} />
              <Row label="İletişim Kişi" value={selected.contact_person} />
              <Row label="Telefon" value={selected.phone} link={`tel:${selected.phone}`} />
              <Row label="E-posta" value={selected.email} link={`mailto:${selected.email}`} />
              <Row
                label="İlgilenilen Ürünler"
                value={(selected.interested_products || []).join(' · ') || '—'}
              />
              <Row label="Tahmini Aylık İhtiyaç" value={selected.monthly_need || '—'} />
              <Row label="Mesaj" value={selected.message || '—'} multiline />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, link, multiline }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-4">
      <div className="eyebrow text-[#5A5145]">{label}</div>
      <div className={`md:col-span-3 text-[#1F1F1F] ${multiline ? 'whitespace-pre-wrap' : ''}`}>
        {link ? (
          <a href={link} className="hover:text-[#B03A2E] underline decoration-1 underline-offset-4">
            {value}
          </a>
        ) : (
          value
        )}
      </div>
    </div>
  );
}
